import config from '../utils/config'
import { includedDateEvents } from '../utils/config'

/**
 * @type {Object}
 * @description These are the default properties for the checkbox component
 */
export const checkboxProps = {
  /**
   * @type {Boolean}
   * @description Controls whether we will display the "indeterminate"
   * type of checkbox, the value will still only be true/false this
   * is visual only
   */
  indeterminate: Boolean,

  /**
   * @type {Boolean}
   * @description Controls whether what the truthy value of the
   * component will be
   */
  trueValue: {
    type: [String, Number, Boolean, Function, Object, Array],
    default: true,
  },

  /**
   * @type {Boolean}
   * @description Controls whether what the falsey value of the
   * component will be
   */
  falseValue: {
    type: [String, Number, Boolean, Function, Object, Array],
    default: false,
  },
}

/**
 * @type {Object}
 * @description These are the default properties for the checkbox and radio
 * input components
 */
export const checkRadioProps = {
  isGroup: { type: Boolean, default: false },
  modelValue: [String, Number, Boolean, Function, Object, Array],
  nativeValue: [String, Number, Boolean, Function, Object, Array],
  label: Boolean,
  type: String,
  disabled: Boolean,
  required: Boolean,
  name: String,
  size: String,
}

export const checkRadioGroupProps = {
  /**
   * @type {Number|String}
   * @description The value of the input
   */
  modelValue: {
    type: [String, Number, Boolean, Function, Object, Array],
    default: undefined,
  },

  /**
   * @type {String}
   * @description The name of the input group.
   */
  name: {
    type: String,
    default: undefined,
  },

  /**
   * @type {Array}
   * @description The options for the input group.
   */
  options: {
    type: Array,
    default: /* istanbul ignore next */ () => [],
  },

  /**
   * @type {Boolean}
   * @description Determines if each option is rendered below
   * the one before.
   */
  vertical: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Same as native required
   */
  required: {
    type: Boolean,
    default: false,
  },

  /**
   * default undefined
   * @type {String}
   * @description Custom validation message for the select component
   * to use, if the input is invalid
   */
  validationMessage: {
    type: String,
    default: 'Please choose an option if you want to proceed.',
  },
}

/**
 * @type {Object}
 * @description These are the default properties for the input component
 */
export const inputProps = {
  /**
   * @type {Number|String}
   * @description The value of the input
   */
  modelValue: {
    default: undefined,
    type: [Number, String],
  },

  /**
   * @type {Boolean}
   * @description Controlls whether to display the password reveal
   * button for password fields
   */
  passwordReveal: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Sets whether the lfet ixon is clickable
   */
  iconClickable: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the input has a max length counter
   */
  hasCounter: {
    type: Boolean,
    default: /* istanbul ignore next */ () => config.defaultInputHasCounter,
  },

  /**
   * @type {Boolean}
   * @description Display loading spinner
   */
  isLoading: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description sets the icon type that we want to display on the
   * left hand side of the input.
   */
  icon: {
    type: String,
    default: undefined,
  },

  /**
   * @type {Boolean}
   * @description Determines if label will be click to edit or not
   */
  inlineEdit: {
    type: Boolean,
    default: false,
  },
}

/**
 * @type {Object}
 * @description These are the default properties for the form element components
 */
export const formElementProps = {
  /**
   * @type {String}
   * @description Any native input type, and textarea
   */
  type: {
    type: String,
    default: 'text',
  },

  /**
   * @type {String}
   * @description Determines the size of the input
   */
  size: {
    type: String,
    default: undefined,
  },

  /**
   * @type {Boolean}
   * @description Determines if the input is expandable or not
   * this flag is intended for textarea inputs
   */
  expandable: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the input should grow in a flex container
   * or not
   */
  grow: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Sets the loading state of the input component this is
   * mainly meant to be used for search inputs or inputs that on blur
   * or keyup fetch resources, you can set the loading state while the
   * request is being handled. This will cause a loading icon to display
   * on the right hand side of the input.
   */
  loading: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description Determines if the input should have auto complete
   * or not
   */
  autocomplete: {
    type: String,
    default: undefined,
  },
}

/**
 * @type {Object}
 * @description These are the default properties for the switch component
 */
export const switchProps = {
  /**
   * @type {Mixed}
   * @description Binding value (v-model)
   */
  modelValue: {
    type: [String, Number, Boolean, Function, Object, Array, Date],
    default: undefined,
  },

  /**
   * @type {Mixed}
   * @description Same as native value
   */
  nativeValue: {
    type: [String, Number, Boolean, Function, Object, Array, Date],
    default: undefined,
  },

  /**
   * @type {Boolean}
   * @description Same as native disabled
   */
  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description
   */
  name: {
    type: String,
    default: undefined,
  },

  /**
   * @type {Boolean}
   * @description Same as native required
   */
  required: {
    type: Boolean,
    default: false,
  },

  /**
   * default undefined
   * @type {String}
   * @description Custom validation message for the select component
   * to use, if the input is invalid
   */
  validationMessage: {
    type: String,
    default: 'Please toggle this switch if you want to proceed.',
  },

  /**
   * @type {Mixed}
   * @description Overrides the returned value when it's checked
   */
  trueValue: {
    type: [String, Number, Boolean, Function, Object, Array, Date],
    default: true,
  },

  /**
   * @type {Mixed}
   * @description Overrides the returned value when it's not checked
   */
  falseValue: {
    type: [String, Number, Boolean, Function, Object, Array, Date],
    default: false,
  },
}

/**
 * @type {Object}
 * @description These are the default props for the rate form field component
 */
export const rateProps = {
  /**
   * @type {Number}
   * @description Binding value (v-model).
   */
  modelValue: {
    type: Number,
    default: 0,
  },

  /**
   * @type {Number|String}
   * @description Sets the max number of stars.
   */
  max: {
    type: [Number, String],
    default: 5,
  },

  /**
   * @type {String}
   * @description Sets the size of icon to use in the component.
   */
  size: {
    type: String,
    default: 'large',
  },

  /**
   * @type {Boolean}
   * @description Sets the rate field to be disabled.
   */
  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the score should be shown.
   */
  showScore: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the text should be shown, this will
   * be overridden by "showScore".
   */
  showText: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description Custom text to show after the score. only Works if
   * show score is enabled.
   */
  customText: {
    type: String,
    default: undefined,
  },

  /**
   * @type {Array}
   * @description Array of texts to display along side the score, we would
   * need a text entry for each possible score value (whole numbers).
   */
  texts: {
    type: Array,
    default: /* istanbul ignore next */ () => [],
  },

  /**
   * @type {Boolean}
   * @description Same as native required
   */
  required: {
    type: Boolean,
    default: false,
  },

  /**
   * default undefined
   * @type {String}
   * @description Custom validation message for the select component
   * to use, if the input is invalid
   */
  validationMessage: {
    type: String,
    default: 'Please choose a rating if you want to proceed.',
  },
}

/**
 * @type {Object}
 * @description These are the default props for the date picker form
 * field component
 */
export const datePickerProps = {
  /**
   * @type {String}
   * @description The value of the input.
   */
  modelValue: {
    default: null,
    required: true,
    /* istanbul ignore next */

    validator(value) {
      return (
        value === null ||
        value instanceof Date ||
        typeof value === 'string' ||
        value instanceof String ||
        value instanceof Array ||
        typeof value === 'number'
      )
    },
  },

  /**
   * @type {Boolean}
   * @description Determines if the date picker should be disabled.
   */
  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Object}
   * @description The configuration of the Flatpickr instance.
   */
  config: {
    type: Object,
    default: /* istanbul ignore next */ () => ({
      defaultDate: null,
      wrap: false,
    }),
  },

  /**
   * @type {Array}
   * @description An array of even names that we want to bind event
   * handlers too.
   */
  events: {
    type: Array,
    default: /* istanbul ignore next */ () => [...includedDateEvents],
  },

  /**
   * @type {Boolean}
   * @description Same as native required
   */
  required: {
    type: Boolean,
    default: false,
  },

  /**
   * default undefined
   * @type {String}
   * @description Custom validation message for the select component
   * to use, if the input is invalid
   */
  validationMessage: {
    type: String,
    default: 'Please choose a date if you want to proceed.',
  },
}

/**
 * @type {Object}
 * @description These are the default props for the validation mixin
 */
export const validationProps = {
  useHtml5Validation: {
    type: Boolean,
    default: /* istanbul ignore next */ () => config.defaultUseHtml5Validation,
  },

  /**
   * default undefined
   * @type {String}
   * @description Custom validation message for the select component
   * to use, if the input is invalid
   */
  validationMessage: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description The custom validation rules for a form input
   */
  customRules: {
    type: Array,
    default: () => [],
  },
}
